import {useEffect, useState} from 'react';
import {Box, Button, Flex, Modal, Overlay, Radio, Stack, Text, useMantineTheme} from '@mantine/core';
import { useDeleteArticlesMutation } from '../../../../../services/ArticleService';
import TableLoadingRow from '../../../../TableLoadingRow';
import { useArticles } from '../../../../../pages/main/articoli/Articoli';

export type RemoveMode = 'CANCELLA' | 'NASCONDI';

interface RemoveArticlesModalProps {
  opened: boolean;
  onClose: () => void;
  count: number;
  skus: string[];
  onSuccess?: () => void;
}

const RemoveArticlesModal = ({ opened, onClose, count, skus, onSuccess }: RemoveArticlesModalProps) => {
  const theme = useMantineTheme();
  const [mode, setMode] = useState<RemoveMode>('CANCELLA');
  const [loading, setLoading] = useState(false);
  const [deleteArticles] = useDeleteArticlesMutation();
  const { getSaveStatus, setSaveStatus } = useArticles();
  const STATUS_KEY = 'DELETE_ARTICLES';

  // Reset status indicator when opening the modal for the current selection
  useEffect(() => {
    if (opened) {
      setSaveStatus(STATUS_KEY, { loading: false, completed: false, error: false });
    }
  }, [opened, setSaveStatus]);

  const handleConfirm = () => {
    if (!skus || skus.length === 0) return;
    setLoading(true);
    // reflect API status for batch delete using a dedicated key
    setSaveStatus(STATUS_KEY, { loading: true, completed: false, error: false });
    deleteArticles({ skus, mode })
      .unwrap()
      .then(() => {
        setSaveStatus(STATUS_KEY, { loading: false, completed: true, error: false });
        onSuccess?.();
      })
      .catch((e) => {
        console.error('Failed to delete articles', e);
        setSaveStatus(STATUS_KEY, { loading: false, completed: false, error: true });
      })
      .finally(() => setLoading(false));
  };

  return (
    <Modal.Root
      opened={opened}
      onClose={onClose}
      centered
      size="sm"
      radius="md"
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
    >
      <Modal.Overlay />
      <Modal.Content aria-modal="true">
        <Box p="md">
          <Modal.Header>
            <Text fw={600}>Rimuovi articoli</Text>
            {!loading && <Modal.CloseButton />}
          </Modal.Header>
          <Modal.Body>
            <Box pos="relative">
              {loading && <Overlay opacity={0} blur={2} zIndex={1000} />}
              <div>
                <Stack gap="md">
                  <Text>
                    Sei sicuro di voler procedere con l'eliminazione di {count} articoli dal sito?
                  </Text>
                  <Radio.Group value={mode} onChange={(v) => setMode(v as RemoveMode)}>
                    <Stack gap="xs">
                      <Radio value="CANCELLA" label="Cancella dal sito" />
{/*
                      <Radio disabled={true} value="NASCONDI" label="Non cancellare dal sito, Ma rendi l'articolo non visibile" />
*/}
                    </Stack>
                  </Radio.Group>
                </Stack>
              </div>
            </Box>
            <Flex justify={"flex-end"} gap={"sm"} mt="md" align={'center'}>
              <TableLoadingRow loadingState={getSaveStatus(STATUS_KEY)} />
              <Button disabled={loading} variant="outline" radius="lg" color={theme.colors.gray[2]} onClick={onClose}>
                Annulla
              </Button>
              <Button variant="outline" radius="lg" bg="red" c={theme.colors.textBlack[1]} onClick={handleConfirm}>
                Cancella
              </Button>
            </Flex>
          </Modal.Body>
        </Box>
      </Modal.Content>
    </Modal.Root>
  );
};

export default RemoveArticlesModal;
